import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;

const ScopeSelect = ({ label, value, onChange, testid, all, own }) => (
  <Field label={label}>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="default">Rol varsayılanı</SelectItem>
        <SelectItem value="all">{all}</SelectItem>
        <SelectItem value="own">{own}</SelectItem>
      </SelectContent>
    </Select>
  </Field>
);

export default function UserDialog({ user, roles, onClose, onSaved }) {
  const { flag, user: me } = useAuth();
  const isNew = !user.id;
  const [f, setF] = useState({
    name: user.name || "", email: user.email || "", password: "", role_key: user.role_key || "satis_personeli",
    title: user.title || "", phone: user.phone || "", is_active: user.is_active ?? true,
    is_sales_rep: user.is_sales_rep ?? false, bonus_rate: user.bonus_rate ?? "",
    customer_scope: user.customer_scope || "default", sales_scope: user.sales_scope || "default",
    takip_view: user.takip_view === true ? "acik" : user.takip_view === false ? "kapali" : "rol",
  });
  const [busy, setBusy] = useState(false);
  const [idemKey] = useState(() => crypto.randomUUID());
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, bonus_rate: f.bonus_rate === "" ? null : Number(f.bonus_rate),
      customer_scope: f.customer_scope === "default" ? null : f.customer_scope, sales_scope: f.sales_scope === "default" ? null : f.sales_scope };
    if (!flag("bonus_view")) delete body.bonus_rate;
    if (!isNew && !body.password) delete body.password;
    if (isNew || !me.is_super || body.takip_view === (user.takip_view === true ? "acik" : user.takip_view === false ? "kapali" : "rol")) delete body.takip_view;
    try {
      if (isNew) await api.post("/users", body, { headers: { "Idempotency-Key": idemKey } });
      else await api.put(`/users/${user.id}`, body);
      toast.success(isNew ? "Kullanıcı oluşturuldu" : "Kullanıcı güncellendi");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const roleOptions = roles.filter((r) => me.is_super || !r.is_super);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Yeni Kullanıcı" : "Kullanıcıyı Düzenle"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label="Ad Soyad"><Input required value={f.name} onChange={set("name")} data-testid="user-form-name" /></Field>
          <Field label="E-posta"><Input required type="email" value={f.email} onChange={set("email")} data-testid="user-form-email" /></Field>
          <Field label={isNew ? "Şifre" : "Yeni Şifre (opsiyonel)"}><Input type="password" required={isNew} minLength={6} value={f.password} onChange={set("password")} data-testid="user-form-password" /></Field>
          <Field label="Rol">
            <Select value={f.role_key} onValueChange={set("role_key")}>
              <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roleOptions.map((r) => <SelectItem key={r.key} value={r.key} data-testid={`user-form-role-${r.key}`}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Ünvan"><Input value={f.title} onChange={set("title")} data-testid="user-form-title" /></Field>
          <Field label="Telefon"><Input value={f.phone} onChange={set("phone")} data-testid="user-form-phone" /></Field>
          {flag("bonus_view") && <Field label="Prim Oranı (%)"><Input type="number" step="0.01" min="0" max="100" value={f.bonus_rate} onChange={set("bonus_rate")} data-testid="user-form-bonus" /></Field>}
          <ScopeSelect label="Cari görünürlüğü" value={f.customer_scope} onChange={set("customer_scope")} testid="user-form-customer-scope" all="Tüm cariler" own="Sadece kendi carileri" />
          <ScopeSelect label="Satış görünürlüğü" value={f.sales_scope} onChange={set("sales_scope")} testid="user-form-sales-scope" all="Tüm satışlar" own="Sadece kendi satışları" />
          {!isNew && me.is_super && <Field label="Takip sekmesini görebilir">
            <Select value={f.takip_view} onValueChange={set("takip_view")}>
              <SelectTrigger data-testid="user-form-takip"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="rol">Rol varsayılanı</SelectItem><SelectItem value="acik">Evet (rolden bağımsız)</SelectItem><SelectItem value="kapali">Hayır (rolden bağımsız)</SelectItem></SelectContent>
            </Select>
          </Field>}
          <div className="col-span-2 flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.is_active} onCheckedChange={set("is_active")} data-testid="user-form-active" />Aktif</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.is_sales_rep} onCheckedChange={set("is_sales_rep")} data-testid="user-form-sales-rep" />Satış temsilcisi</label>
          </div>
          <DialogFooter className="col-span-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="user-form-cancel">Vazgeç</Button>
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="user-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Kaydet</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
